export const normalizeCompanyName = name =>
  (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export const parseImsatCompanies = infoOptionList => {
  if (!infoOptionList) return []

  const { API_URL, CODE, NAME, IMSAT_COMPANIES } = infoOptionList
  const apiUrl = API_URL

  let companies = []

  if (IMSAT_COMPANIES) {
    try {
      const parsed = JSON.parse(IMSAT_COMPANIES)
      if (Array.isArray(parsed)) {
        companies = parsed
      }
    } catch (error) {
      console.warn('Invalid IMSAT_COMPANIES JSON in info_option', error)
    }
  }

  if (companies.length === 0 && CODE && NAME) {
    companies = [{ code: CODE, name: NAME }]
  }

  return companies
    .filter(company => company?.code && company?.name)
    .map(company => ({
      code: company.code,
      name: company.name,
      cpctmCode: company.cpctmCode || null,
      apiUrl: company.apiUrl || apiUrl,
      mergeLocal: !!company.mergeLocal,
    }))
}

const findMainCpctmCompany = companyList =>
  companyList.find(company => {
    const normalizedName = normalizeCompanyName(company.name)
    return (
      normalizedName.includes('phat trien cong nghe va thiet bi mo') ||
      normalizedName.includes('thiet bi mo')
    )
  })

export const findMainCpctmCompanyCode = companyList =>
  findMainCpctmCompany(companyList)?.code

const resolveMainCompanyImsatCode = (
  infoOptionList,
  companyList,
  cpctmCode
) => {
  const { CPCTM_IMSAT_CODE } = infoOptionList || {}
  if (CPCTM_IMSAT_CODE) {
    return CPCTM_IMSAT_CODE
  }

  const mainCompany = companyList.find(company => company.code === cpctmCode)
  const imsatCompanies = parseImsatCompanies(infoOptionList)

  const mergeEntry = imsatCompanies.find(
    company =>
      company.mergeLocal &&
      (company.cpctmCode === cpctmCode ||
        (mainCompany &&
          normalizeCompanyName(company.name) ===
            normalizeCompanyName(mainCompany.name)))
  )
  if (mergeEntry?.code) {
    return mergeEntry.code
  }

  const nameMatch = mainCompany
    ? imsatCompanies.find(
        company =>
          normalizeCompanyName(company.name) ===
          normalizeCompanyName(mainCompany.name)
      )
    : null
  if (nameMatch?.code) {
    return nameMatch.code
  }

  return cpctmCode
}

const SCIENCE_INSTITUTE_KEYWORD = 'vien khoa hoc cong nghe mo'

const IMSAT_SUBSIDIARY_KEYWORDS = [
  SCIENCE_INSTITUTE_KEYWORD,
  'trung tam an toan mo',
]

export const findScienceInstituteCompany = (companyList = []) =>
  companyList.find(company =>
    normalizeCompanyName(company.name).includes(SCIENCE_INSTITUTE_KEYWORD)
  )

export const findSelectedImsatCompany = (imsatCompanies, selectedKey) =>
  imsatCompanies.find(
    company => company.code === selectedKey || company.cpctmCode === selectedKey
  )

export const resolveWorkScheduleMenuKey = (selectedKey, imsatCompanies) => {
  if (!selectedKey) {
    return null
  }

  const selectedImsat = findSelectedImsatCompany(imsatCompanies, selectedKey)
  return String(selectedImsat?.code || selectedKey)
}

export const getImsatCpctmCodesToHide = imsatCompanies =>
  new Set(
    imsatCompanies
      .filter(company => !company.mergeLocal)
      .flatMap(company => [company.cpctmCode].filter(Boolean))
  )

export const getWorkScheduleVisibleMenuKeys = ({ permissionViewList = [] }) => {
  const seen = new Set()

  return permissionViewList.reduce((visibleKeys, permission) => {
    const key = String(permission?.access_type || '')
    if (!key || seen.has(key)) {
      return visibleKeys
    }

    seen.add(key)
    visibleKeys.push(key)
    return visibleKeys
  }, [])
}

export const buildWorkScheduleMenuItemsFromPermissions = ({
  permissionViewList = [],
  companyList = [],
  imsatCompanies = [],
}) => {
  const seen = new Set()
  const menuItems = []

  permissionViewList.forEach(permission => {
    const key = String(permission?.access_type || '')
    if (!key || seen.has(key)) {
      return
    }

    seen.add(key)

    const localCompany = companyList.find(company => company.code === key)
    const imsatCompany = findSelectedImsatCompany(imsatCompanies, key)

    menuItems.push({
      key,
      name: localCompany?.name || imsatCompany?.name || key,
    })
  })

  return menuItems
}

export const isWorkScheduleParentSource = ({
  companyList,
  selectedKey,
  imsatCompanies,
  optionCode,
}) => {
  if (
    isLocalImsatSubsidiarySelected(companyList, selectedKey, imsatCompanies)
  ) {
    return false
  }

  const selectedImsat = findSelectedImsatCompany(imsatCompanies, selectedKey)
  return !!selectedImsat || selectedKey === optionCode
}

const isLocalCompanyTabHidden = (imsatCompanies, companyCode) =>
  imsatCompanies
    .filter(company => !company.mergeLocal)
    .some(company => company.cpctmCode === companyCode)

export const getDefaultWorkScheduleCompanyKey = ({
  companyList = [],
  imsatCompanies = [],
  currentUserCompanyCode,
  permissionViewList,
}) => {
  if (currentUserCompanyCode) {
    const canShowLocalTab =
      !isLocalCompanyTabHidden(imsatCompanies, currentUserCompanyCode) &&
      companyList.some(company => company.code === currentUserCompanyCode) &&
      (!permissionViewList?.length ||
        permissionViewList.some(
          permission => permission.access_type === currentUserCompanyCode
        ))

    if (canShowLocalTab) {
      return currentUserCompanyCode
    }

    const imsatMatch = findSelectedImsatCompany(
      imsatCompanies,
      currentUserCompanyCode
    )
    if (imsatMatch) {
      return imsatMatch.code
    }

    return currentUserCompanyCode
  }

  const scienceInstitute = findScienceInstituteCompany(companyList)
  if (!scienceInstitute) {
    return null
  }

  const imsatMatch = findSelectedImsatCompany(
    imsatCompanies,
    scienceInstitute.code
  )
  if (imsatMatch) {
    return imsatMatch.code
  }

  return scienceInstitute.code
}

export const resolveLocalWorkScheduleCompanyKey = (
  companyList,
  selectedKey,
  imsatCompanies
) => {
  const selectedImsat = findSelectedImsatCompany(imsatCompanies, selectedKey)
  if (selectedImsat?.cpctmCode) {
    return selectedImsat.cpctmCode
  }

  return selectedKey
}

export const canCreateWorkSchedule = ({
  companyList,
  imsatCompanies,
  selectedKey,
  currentUserCompanyCode,
}) => {
  if (!selectedKey || !currentUserCompanyCode) {
    return false
  }

  return (
    resolveLocalWorkScheduleCompanyKey(
      companyList,
      selectedKey,
      imsatCompanies
    ) === currentUserCompanyCode
  )
}

export const isLocalImsatSubsidiarySelected = (
  companyList,
  selectedKey,
  imsatCompanies
) => {
  const localCompanyKey = resolveLocalWorkScheduleCompanyKey(
    companyList,
    selectedKey,
    imsatCompanies
  )
  const localCompany = companyList.find(
    company => company.code === localCompanyKey
  )

  return isImsatSubsidiaryCompany(localCompany)
}

export const filterWorkSchedulesByDateRange = (
  schedules = [],
  fromDate,
  toDate
) => {
  if (!fromDate || !toDate) {
    return schedules
  }

  const from = new Date(fromDate)
  from.setHours(0, 0, 0, 0)
  const to = new Date(toDate)
  to.setHours(23, 59, 59, 999)

  return schedules.filter(item => {
    if (!item?.start_at) {
      return false
    }

    const startAt = new Date(item.start_at)
    return startAt >= from && startAt <= to
  })
}

export const fetchWorkScheduleList = async ({
  workScheduleStore,
  parentWorkScheduleStore,
  fromDate,
  toDate,
  selectedKey,
  companyList,
  imsatCompanies,
  infoOptionList,
  currentUserCompanyCode,
  isPublic = false,
}) => {
  if (!selectedKey) {
    return []
  }

  const mergedLocalImsatConfig = getMergedLocalImsatConfig(
    infoOptionList,
    companyList,
    selectedKey,
    currentUserCompanyCode
  )
  const selectedImsatCompany = findSelectedImsatCompany(
    imsatCompanies,
    selectedKey
  )
  const isImsatCompanySelected = !!selectedImsatCompany
  const optionCode = infoOptionList?.CODE
  const isParentCompanySelected =
    isImsatCompanySelected || selectedKey === optionCode

  let schedules = []

  if (
    isLocalImsatSubsidiarySelected(companyList, selectedKey, imsatCompanies)
  ) {
    const localCompanyKey = resolveLocalWorkScheduleCompanyKey(
      companyList,
      selectedKey,
      imsatCompanies
    )

    schedules = await workScheduleStore.getWorkSchedule(
      fromDate,
      toDate,
      true,
      localCompanyKey
    )
  } else if (mergedLocalImsatConfig) {
    const localData = await workScheduleStore.getWorkSchedule(
      fromDate,
      toDate,
      true,
      selectedKey
    )

    let imsatData = []
    try {
      imsatData = await parentWorkScheduleStore.getWorkSchedule(
        fromDate,
        toDate,
        mergedLocalImsatConfig.imsatCode,
        mergedLocalImsatConfig.apiUrl
      )
    } catch (imsatError) {
      console.warn('Không lấy được lịch IMSAT cho công ty chính', imsatError)
    }

    schedules = mergeWorkSchedules(localData, imsatData)
  } else if (isImsatCompanySelected) {
    schedules = await parentWorkScheduleStore.getWorkSchedule(
      fromDate,
      toDate,
      selectedImsatCompany.code,
      selectedImsatCompany.apiUrl
    )
  } else if (isParentCompanySelected) {
    schedules = await parentWorkScheduleStore.getWorkSchedule(fromDate, toDate)
  } else if (isPublic) {
    schedules = await workScheduleStore.getPublicWorkSchedule(fromDate, toDate)
  } else {
    schedules = await workScheduleStore.getWorkSchedule(
      fromDate,
      toDate,
      true,
      selectedKey
    )
  }

  return filterWorkSchedulesByDateRange(schedules, fromDate, toDate)
}

export const sortWorkScheduleMenuItems = (menuItems, preferredKey) => {
  if (!preferredKey || !menuItems?.length) {
    return menuItems
  }

  const preferredIndex = menuItems.findIndex(
    item => String(item?.key) === String(preferredKey)
  )
  if (preferredIndex <= 0) {
    return menuItems
  }

  const preferredItem = menuItems[preferredIndex]
  return [
    preferredItem,
    ...menuItems.slice(0, preferredIndex),
    ...menuItems.slice(preferredIndex + 1),
  ]
}

const isImsatSubsidiaryCompany = company => {
  if (!company?.name) {
    return false
  }
  const normalizedName = normalizeCompanyName(company.name)
  return IMSAT_SUBSIDIARY_KEYWORDS.some(keyword =>
    normalizedName.includes(keyword)
  )
}

export const getMergedLocalImsatConfig = (
  infoOptionList,
  companyList,
  selectedKey,
  currentUserCompanyCode
) => {
  if (!selectedKey || !infoOptionList?.API_URL) {
    return null
  }

  const { API_URL, CPCTM_CODE } = infoOptionList
  const selectedCompany = companyList.find(
    company => company.code === selectedKey
  )

  if (isImsatSubsidiaryCompany(selectedCompany)) {
    return null
  }

  const mainCpctmCode =
    CPCTM_CODE ||
    findMainCpctmCompanyCode(companyList) ||
    currentUserCompanyCode

  const shouldMerge =
    selectedKey === mainCpctmCode ||
    (selectedKey === currentUserCompanyCode &&
      !isImsatSubsidiaryCompany(selectedCompany))

  if (!shouldMerge) {
    return null
  }

  return {
    imsatCode: resolveMainCompanyImsatCode(
      infoOptionList,
      companyList,
      selectedKey
    ),
    apiUrl: API_URL,
    cpctmCode: selectedKey,
  }
}

export const mergeWorkSchedules = (
  localSchedules = [],
  imsatSchedules = []
) => {
  const merged = new Map()

  localSchedules.forEach(item => {
    merged.set(item.schedule_code, { ...item, _isImsatSource: false })
  })

  imsatSchedules.forEach(item => {
    if (!merged.has(item.schedule_code)) {
      merged.set(item.schedule_code, { ...item, _isImsatSource: true })
    }
  })

  return Array.from(merged.values()).sort(
    (first, second) => new Date(first.start_at) - new Date(second.start_at)
  )
}

export const shouldUseParentWorkScheduleView = (
  item,
  isParentCompanySelected
) => item._isImsatSource ?? isParentCompanySelected

export const getWorkScheduleViewPath = (item, isParentCompanySelected) => {
  const useParentView = shouldUseParentWorkScheduleView(
    item,
    isParentCompanySelected
  )

  if (useParentView) {
    return `/company-work-schedule/parent/view/${item.schedule_code}`
  }

  return `/company-work-schedule/view/${item.schedule_code}`
}

export const enrichImsatCompaniesWithCpctmCodes = (
  imsatCompanies,
  companyList
) =>
  imsatCompanies.map(imsatCompany => {
    if (imsatCompany.cpctmCode) {
      return imsatCompany
    }

    const matchedCompany = companyList.find(
      company =>
        normalizeCompanyName(company.name) ===
        normalizeCompanyName(imsatCompany.name)
    )

    return matchedCompany
      ? { ...imsatCompany, cpctmCode: matchedCompany.code }
      : imsatCompany
  })

export const buildImsatWorkScheduleCompanies = (
  infoOptionList,
  companyList,
  permissionViewList,
  currentUserCompanyCode
) => {
  let companies = enrichImsatCompaniesWithCpctmCodes(
    parseImsatCompanies(infoOptionList).filter(company => !company.mergeLocal),
    companyList
  )

  const { API_URL, CODE, NAME } = infoOptionList || {}
  const configuredCpctmCodes = new Set(
    companies.map(company => company.cpctmCode).filter(Boolean)
  )

  permissionViewList.forEach(permission => {
    const company = companyList.find(
      item => item.code === permission.access_type
    )
    if (!company || company.code === currentUserCompanyCode) {
      return
    }
    if (configuredCpctmCodes.has(company.code)) {
      return
    }

    const isImsatSubsidiary = isImsatSubsidiaryCompany(company)
    if (!isImsatSubsidiary) {
      return
    }

    const legacyMatch =
      CODE &&
      NAME &&
      normalizeCompanyName(company.name) === normalizeCompanyName(NAME)

    companies.push({
      code: legacyMatch ? CODE : company.code,
      name: company.name,
      cpctmCode: company.code,
      apiUrl: API_URL,
    })
    configuredCpctmCodes.add(company.code)
  })

  const companiesByName = new Map()
  companies.forEach(company => {
    const normalizedName = normalizeCompanyName(company.name)
    const existingCompany = companiesByName.get(normalizedName)
    if (!existingCompany || (!existingCompany.cpctmCode && company.cpctmCode)) {
      companiesByName.set(normalizedName, company)
    }
  })

  return Array.from(companiesByName.values())
}
